/// <reference types="vite/client" />
import { createClient } from '@supabase/supabase-js';
import type { Vessel, Survey } from '../types';

const url = import.meta.env.VITE_SUPABASE_URL;
const key = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const isSupabaseConfigured = !!url && !!key;

if (!isSupabaseConfigured) {
  console.warn('Supabase environment variables not set. Auth and cloud sync disabled. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your build environment.');
}

export const supabase = createClient(url || 'https://placeholder.supabase.co', key || 'placeholder-key', {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
  },
});

export async function getVessels(): Promise<Vessel[]> {
  if (!isSupabaseConfigured) return [];
  const { data, error } = await supabase.from('vessels').select('*').order('name', { ascending: true });
  if (error) throw new Error(error.message);
  return (data || []) as Vessel[];
}

export async function getVessel(id: string): Promise<Vessel | null> {
  if (!isSupabaseConfigured) return null;
  const { data, error } = await supabase.from('vessels').select('*').eq('id', id).single();
  if (error) throw new Error(error.message);
  return data as Vessel | null;
}

export async function createVessel(vessel: Omit<Vessel, 'id' | 'created_at'>): Promise<Vessel> {
  if (!isSupabaseConfigured) throw new Error('Supabase not configured');
  const { data, error } = await supabase.from('vessels').insert(vessel).select().single();
  if (error) throw new Error(error.message);
  if (!data) throw new Error('Failed to create vessel');
  return data as Vessel;
}

export async function updateVessel(id: string, updates: Partial<Vessel>): Promise<void> {
  if (!isSupabaseConfigured) throw new Error('Supabase not configured');
  const { error } = await supabase.from('vessels').update(updates).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deleteVessel(id: string): Promise<void> {
  if (!isSupabaseConfigured) throw new Error('Supabase not configured');
  const { error } = await supabase.from('vessels').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

export async function saveSurvey(survey: Omit<Survey, 'id' | 'created_at'>): Promise<Survey> {
  if (!isSupabaseConfigured) throw new Error('Supabase not configured');
  const { data, error } = await supabase.from('surveys').insert(survey).select().single();
  if (error) throw new Error(error.message);
  if (!data) throw new Error('Failed to save survey');
  return data as Survey;
}

export async function getSurveys(vesselId?: string): Promise<Survey[]> {
  if (!isSupabaseConfigured) return [];
  let q = supabase.from('surveys').select('*').order('survey_date', { ascending: false });
  if (vesselId) q = q.eq('vessel_id', vesselId);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data || []) as Survey[];
}

export async function updateSurvey(id: string, updates: Partial<Survey>): Promise<void> {
  if (!isSupabaseConfigured) throw new Error('Supabase not configured');
  const { error } = await supabase.from('surveys').update(updates).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deleteSurvey(id: string): Promise<void> {
  if (!isSupabaseConfigured) throw new Error('Supabase not configured');
  const { error } = await supabase.from('surveys').delete().eq('id', id);
  if (error) throw new Error(error.message);
}
